/**
 * Audit Log Types
 * 
 * ระบบบันทึกประวัติการใช้งาน
 */

// ============================================
// Action Types
// ============================================

export type AuditAction = 
  // CRUD Operations
  | 'create'
  | 'update'
  | 'delete'
  | 'view'
  // Authentication
  | 'login'
  | 'logout'
  | 'login_failed'
  | 'register'
  | 'change_password'
  | 'reset_password'
  // Approval & Status
  | 'approve'
  | 'reject'
  | 'submit'
  | 'cancel'
  | 'complete'
  // Transactions
  | 'deposit'
  | 'withdraw'
  | 'transfer'
  | 'refund'
  // Settings
  | 'update_setting'
  | 'refresh_cache'
  // Other
  | 'send'
  | 'error'
  | 'unknown'

export const AuditActionLabels: Record<AuditAction, string> = {
  // CRUD
  create: 'สร้าง',
  update: 'แก้ไข',
  delete: 'ลบ',
  view: 'ดู',
  // Auth
  login: 'เข้าสู่ระบบ',
  logout: 'ออกจากระบบ',
  login_failed: 'เข้าสู่ระบบไม่สำเร็จ',
  register: 'สมัครสมาชิก',
  change_password: 'เปลี่ยนรหัสผ่าน',
  reset_password: 'รีเซ็ตรหัสผ่าน',
  // Approval
  approve: 'อนุมัติ',
  reject: 'ปฏิเสธ',
  submit: 'ส่ง',
  cancel: 'ยกเลิก',
  complete: 'เสร็จสิ้น',
  // Transactions
  deposit: 'ฝากเงิน',
  withdraw: 'ถอนเงิน',
  transfer: 'โอนเงิน',
  refund: 'คืนเงิน',
  // Settings
  update_setting: 'อัปเดตการตั้งค่า',
  refresh_cache: 'รีเฟรช Cache',
  // Other
  send: 'ส่ง',
  error: 'เกิดข้อผิดพลาด',
  unknown: 'ไม่ทราบ',
}

// ============================================
// Entity Types
// ============================================

export type EntityType = 
  | 'User'
  | 'Member'
  | 'Admin'
  | 'Setting'
  | 'AppConfig'
  | 'Promotion'
  | 'DepositWithdraw'
  | 'BankAccount'
  | 'Notification'
  | 'System'
  | 'Unknown'

export const EntityTypeLabels: Record<EntityType, string> = {
  User: 'ผู้ใช้',
  Member: 'สมาชิก',
  Admin: 'แอดมิน',
  Setting: 'การตั้งค่า',
  AppConfig: 'ตั้งค่าระบบ',
  Promotion: 'โปรโมชั่น',
  DepositWithdraw: 'ฝาก-ถอน',
  BankAccount: 'บัญชีธนาคาร',
  Notification: 'การแจ้งเตือน',
  System: 'ระบบ',
  Unknown: 'ไม่ทราบ',
}

// ============================================
// Severity & Status
// ============================================

export type AuditSeverity = 'info' | 'warning' | 'error' | 'critical'
export type AuditStatus = 'success' | 'failed'

export const SeverityLabels: Record<AuditSeverity, string> = {
  info: 'ข้อมูล',
  warning: 'คำเตือน',
  error: 'ข้อผิดพลาด',
  critical: 'วิกฤติ',
}

export const StatusLabels: Record<AuditStatus, string> = {
  success: 'สำเร็จ',
  failed: 'ล้มเหลว',
}

// ============================================
// Main Types
// ============================================

export interface AuditLogChange {
  field: string
  fieldLabel?: string
  oldValue?: string | number | boolean | null
  newValue?: string | number | boolean | null
}

/**
 * Parse Date type - รองรับทั้ง ISO string และ Parse Date object
 */
export type ParseDate = string | { __type: 'Date'; iso: string } | Date

export interface AuditLog {
  id: string
  
  // WHO
  actorId: string | null
  actorName: string
  actorUsername: string
  actorRole: string
  actorEmail?: string
  
  // WHAT
  action: AuditAction
  entityType: EntityType
  entityId: string
  entityName: string
  description: string
  targetMemberId?: string // 🆕 member id ที่ถูกทำ action
  
  // WHEN
  timestamp: ParseDate
  
  // WHERE
  ipAddress?: string
  userAgent?: string
  
  // 🆕 DEVICE TRACKING
  deviceId?: string | null
  fingerprintId?: string | null // ✅ Fingerprint ID สำหรับ device tracking
  
  // HOW
  changes: AuditLogChange[]
  metadata?: Record<string, any>
  
  // STATUS
  category: string
  severity: AuditSeverity
  status: AuditStatus
  errorMessage?: string
  
  // Timestamps
  createdAt: ParseDate
  updatedAt?: ParseDate
}

// ============================================
// Filter & Params
// ============================================

export interface AuditLogFilters {
  dateFrom?: string
  dateTo?: string
  entityType?: EntityType | ''
  entityId?: string // สำหรับ filter logs ของ member/entity คนใดคนหนึ่ง
  targetMemberId?: string // 🆕 สำหรับ filter กิจกรรมที่ทำกับ member คนใดคนหนึ่ง
  fingerprintId?: string // 🆕 สำหรับ filter กิจกรรมของ fingerprint ตัวนั้น
  action?: AuditAction | ''
  severity?: AuditSeverity | ''
  status?: AuditStatus | ''
  searchText?: string
}

export interface AuditLogParams {
  page: number
  limit: number
}

// ============================================
// Statistics
// ============================================

export interface AuditLogStatistics {
  total: number
  byAction: Record<string, number>
  byEntity: Record<string, number>
  bySeverity: Record<string, number>
  byStatus: Record<string, number>
  topActors: { name: string; count: number }[]
}

// ============================================
// Create Audit Log Input
// ============================================

export interface CreateAuditLogInput {
  action: AuditAction
  entityType: EntityType
  entityId: string
  entityName: string
  description: string
  changes?: AuditLogChange[]
  metadata?: Record<string, any>
  severity?: AuditSeverity
  status?: AuditStatus
  errorMessage?: string
}

